import {
  Table,
  ProgressBar,
  Tooltip,
  IconButton,
  tailwindOverride,
} from '@getsynapse/design-system';
import moment from 'moment';
import intl from 'react-intl-universal';
import { useHistory } from 'react-router-dom';
import {
  ProgramLinkedProject,
  SortingType,
  PilotPagesToTrack,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';
import classnames from 'classnames';
import emptyProjectsTable from 'assets/icons/empty-projects.svg';
import { getUserAccessOnProject } from 'Pages/ProjectsListPage/helpers/getUserParticipantType';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import UnlinkContent from 'assets/images/unlink-content.svg';
import { selectIsUserLd, isUserOrganizationAdmin } from 'state/User/userSlice';
import { selectUserById } from 'state/UsersManagement/usersManagementSlice';
import { selectLearningTeams } from 'state/LearningTeams/learningTeamsSlice';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import {
  selectProgramProjectsTableSearch,
  selectProgramProjectsTableFilters,
  setProgramProjectsSortingOptions,
  selectProjectsForProgramSliceStatus,
  unlinkProjectFromProgram,
  getProgramId,
  fetchProjectsForProgram,
  updateProjectInProgramList,
} from 'state/Program/programSlice';
import {
  setProjectId,
  fetchProject,
  resetProject,
  getOriginalProjectData,
} from 'state/Project/projectSlice';
import TableEmptyErrorState from 'Molecules/TableEmptyErrorState/TableEmptyErrorState';
import noSearchResults from 'assets/images/no-projects-found-table.svg';
import noFilterResults from 'assets/images/empty-filtered-projects-table.svg';
import RenderNoRecords from 'Atoms/NoRecords/NoRecords';
import {
  DATE,
  PROGRAM_PROJECT_FIELDS,
  PATHS,
  SLICE_STATUS,
  PROJECT_STATUS,
  PROJECT_USER_ACTIONS,
  PROJECT_PERMISSIONS_BY_LEVEL,
} from 'utils/constants';
import { formatProjectNumber } from 'utils/formatters';
import { getStatusPill } from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import ProjectSidePanel from 'Organisms/ProjectSidePanel/ProjectSidePanel';
import {
  decideCurrentProgressValueBasedOnProjectStatus,
  decideMaxValueBasedOnProjectStatus,
} from './ProgramProjectsTableHelpers';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell';
import { useUpdateProjectStatus } from 'Pages/ProjectsListPage/hooks/useUpdateProjectStatus/useUpdateProjectStatus';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState, useCallback } from 'react';
import CloseProject from 'Pages/ProjectPage/components/CloseProject/CloseProject';
import CancelProject from 'Pages/ProjectPage/components/CancelProject/CancelProject';
import PutProjectOnHoldModal from 'Pages/ProjectPage/components/PutProjectOnHoldModal/PutProjectOnHoldModal';
import { bulkUpdateProjects } from 'api/projects';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';

const ProgramProjectsTable = ({
  projects,
  userHasWriteAccess = false,
  isReadOnly = false,
}: {
  projects: ProgramLinkedProject[];
  userHasWriteAccess: boolean;
  isReadOnly?: boolean;
}) => {
  const handleSort = (orderByParam: string, order: SortingType) => {
    dispatch(setProgramProjectsSortingOptions(orderByParam, order));
  };
  const { inlineEditingProjectStatusProgramDetail } = useFlags();

  const history = useHistory();
  const dispatch = useDispatch();
  const { handleSaveProjectStatus, getProjectStatuses } =
    useUpdateProjectStatus();

  const [displayCancelModal, setDisplayCancelModal] = useState(false);
  const [displayOnHoldModal, setDisplayOnHoldModal] = useState(false);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);

  const searchValue = useSelector(selectProgramProjectsTableSearch);
  const programId = useSelector(getProgramId);
  const filterValues = useSelector(selectProgramProjectsTableFilters);
  const projectsForProgramStatus = useSelector(
    selectProjectsForProgramSliceStatus
  );

  const userData = useSelector(selectUserById);
  const ldTeams = useSelector(selectLearningTeams);
  const isTableEmpty = projects.length === 0;
  const isAdmin = useSelector(isUserOrganizationAdmin);
  const isLDUser = useSelector(selectIsUserLd);
  const selectedProject = useSelector(getOriginalProjectData);

  const unlinkProject = (projectId: string) => {
    dispatch(unlinkProjectFromProgram({ projectId }));
  };

  const onSelectProject = async (projectId: string) => {
    await dispatch(setProjectId(projectId));
  };

  const onRefresh = useCallback(() => {
    dispatch(fetchProjectsForProgram(programId));
  }, [dispatch, programId]);

  const onUpdateSuccess = useCallback(
    (projectId: string, newStatus: string) => {
      dispatch(
        updateProjectInProgramList({
          projectId,
          updatedProject: { status: newStatus },
        })
      );
    },
    [dispatch]
  );

  const onOpenCancelModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCancelModal(true);
    },
    [dispatch]
  );

  const onOpenOnHoldModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayOnHoldModal(true);
    },
    [dispatch]
  );

  const onOpenCloseModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCloseModal(true);
    },
    [dispatch]
  );

  const handleCloseCancelModal = useCallback(() => {
    setDisplayCancelModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseOnHoldModal = useCallback(() => {
    setDisplayOnHoldModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseCloseModal = useCallback(() => {
    setDisplayCloseModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleConfirmStatusChange = useCallback(
    async (
      status: string,
      extraFields?: {
        cancelReason?: string;
        cancellationDate?: string;
        holdReason?: string;
        finalComments?: string;
        closedDate?: string;
        isArchived?: boolean;
      }
    ) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status,
          ...extraFields,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          if (status === PROJECT_STATUS.CANCELED) {
            setDisplayCancelModal(false);
          } else if (status === PROJECT_STATUS.ON_HOLD) {
            setDisplayOnHoldModal(false);
          } else if (status === PROJECT_STATUS.CLOSED) {
            setDisplayCloseModal(false);
          }
          onUpdateSuccess(selectedProject.id, status);
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, onUpdateSuccess]
  );

  const handleConfirmCancelProject = useCallback(
    (cancelReason: string, archive: boolean) => {
      handleConfirmStatusChange(PROJECT_STATUS.CANCELED, {
        cancelReason,
        cancellationDate: new Date().toISOString(),
        isArchived: archive,
      });
    },
    [handleConfirmStatusChange]
  );

  const handleConfirmOnHoldProject = useCallback(
    (holdReason: string) => {
      handleConfirmStatusChange(PROJECT_STATUS.ON_HOLD, { holdReason });
    },
    [handleConfirmStatusChange]
  );

  const handleConfirmCloseProject = useCallback(
    (comments: string, closingDate: string, archive: boolean) => {
      handleConfirmStatusChange(PROJECT_STATUS.CLOSED, {
        finalComments: comments,
        closedDate: closingDate,
        isArchived: archive,
      });
    },
    [handleConfirmStatusChange]
  );

  const getStatusComponent = (
    project: ProgramLinkedProject,
    canEditStatus: boolean,
    hasEditPermission: boolean,
    isOnlyStatusViewMode: boolean
  ) => {
    return (
      <StatusTableRowCell
        value={project.status || ''}
        customizedValue={getStatusPill}
        options={getProjectStatuses(project)}
        isEditable={!isOnlyStatusViewMode}
        className='w-20'
        isTaskProjectDisabled={false}
        canInlineEditTask={true}
        onSave={(newValue) => {
          handleSaveProjectStatus(
            newValue,
            canEditStatus,
            project,
            onOpenCancelModal,
            onOpenOnHoldModal,
            onOpenCloseModal,
            onRefresh,
            onUpdateSuccess
          );
        }}
        canEdit={hasEditPermission}
        entityType={StatusInlineEditingEntityType.PROJECT}
        userPilotEventOrigin={PilotPagesToTrack.PROGRAM_DETAIL}
        isArchived={project.is_archived}
      />
    );
  };

  const onRowClick = (projectId: string, hasAccess: boolean) => {
    if (inlineEditingProjectStatusProgramDetail) return;

    if (hasAccess) {
      history.push(`${PATHS.PROJECT_PAGE}/${projectId}`, {
        from: `${PATHS.PROGRAM_PAGE}/${programId}`,
      });
    } else {
      dispatch(
        showNotificationBanner({
          notificationVariant: 'error',
          notificationText: intl.get('PROGRAM_PAGE.PROJECT_NO_ACCESS'),
        })
      );
    }
  };

  const formatDate = (date: string) => {
    return moment(date.split('T')[0]).format(DATE.SHORT_FORMAT);
  };

  return (
    <div
      className={tailwindOverride(
        'w-full transition-height',
        { 'border border-neutral-lighter-two': !isTableEmpty },
        { 'overflow-hidden': isTableEmpty }
      )}
    >
      <ProjectSidePanel onClosePanel={() => onSelectProject('')} />
      <div
        className={tailwindOverride('w-full', {
          'overflow-x-auto': !isTableEmpty,
          'overflow-hidden border-r border-neutral-lighter-two': isTableEmpty,
        })}
      >
        <Table
          className={classnames('relative w-full', {
            'max-w-full border-0 ': !isTableEmpty,
          })}
          data-cy='program-projects-table'
          canSelectRows={false}
          isSelectRowCellSticky
          data={{
            headData: {
              stickyHeader: true,
              handleSort,
              headCells: [
                {
                  columnName: PROGRAM_PROJECT_FIELDS.PROJECT_NUMBER,
                  content: intl.get(
                    'PROGRAM_PAGE.TABLE.COLUMNS.PROJECT_NUMBER'
                  ),
                  className: 'font-semibold w-24 truncate z-2',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.TITLE,
                  content: intl.get('PROGRAM_PAGE.TABLE.COLUMNS.TITLE'),
                  className: 'font-semibold max-w-60 min-w-60 left-0 z-2',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.STATUS,
                  content: intl.get('PROGRAM_PAGE.TABLE.COLUMNS.STATUS'),
                  className: `font-semibold ${
                    inlineEditingProjectStatusProgramDetail ? 'w-20' : 'w-12'
                  } left-60 z-2`,
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.PRIORITY,
                  content: intl.get('PROGRAM_PAGE.TABLE.COLUMNS.PRIORITY'),
                  className: 'font-semibold w-30 py-3',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.PROGRESS,
                  content: intl.get('PROGRAM_PAGE.TABLE.COLUMNS.PROGRESS'),
                  className: 'font-semibold w-50 min-w-40 py-3',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.START_DATE,
                  content: intl.get('PROGRAM_PAGE.TABLE.COLUMNS.START_DATE'),
                  className: 'font-semibold w-30 py-3',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.TARGET_COMPLETION_DATE,
                  content: intl.get(
                    'PROGRAM_PAGE.TABLE.COLUMNS.TARGET_COMPLETION_DATE'
                  ),
                  className: 'font-semibold w-30 py-3',
                },
                {
                  columnName: PROGRAM_PROJECT_FIELDS.ACTUAL_COMPLETION_DATE,
                  content: intl.get(
                    'PROGRAM_PAGE.TABLE.COLUMNS.ACTUAL_COMPLETION_DATE'
                  ),
                  className: 'font-semibold w-30 py-3',
                },
                {
                  content: <div></div>,
                  className: classnames('h-10 bg-primary-lightest z-2', {
                    'w-16 right-0': !isTableEmpty,
                  }),
                },
              ],
            },
            rows: projects.map(
              (project: ProgramLinkedProject, index: number) => {
                const startDate = formatDate(project.startDate);
                const targetCompletionDate = formatDate(
                  project.targetCompletionDate
                );
                const actualCompletionDate =
                  project.status === PROJECT_STATUS.COMPLETED
                    ? formatDate(project.actualDate ?? '')
                    : '-';
                const {
                  number_of_completed_tasks: completedTasksCount,
                  number_of_not_completed_tasks: incompletedTasksCount,
                } = project;

                const isOdd = index % 2 !== 0;

                // `isReadOnly` (component prop) means the program is archived.
                // The per-project access flag is aliased to `projectIsReadOnly`
                // to avoid shadowing that prop within this row.
                const {
                  isReadOnly: projectIsReadOnly,
                  hasAccess,
                  permissionLevel,
                } = getUserAccessOnProject(project, userData, ldTeams);

                const availableActions =
                  PROJECT_PERMISSIONS_BY_LEVEL[permissionLevel];
                const canUpdateProject = availableActions.includes(
                  PROJECT_USER_ACTIONS.UPDATE_PROJECT
                );
                const hasEditPermission = canUpdateProject || isAdmin;
                const canEditStatus = hasEditPermission && !project.is_archived;
                const isOnlyStatusViewMode = !canEditStatus;

                const stickyCellsStyles = (className: string) =>
                  classnames(
                    'border-transparent',
                    {
                      'sticky z-1': !isTableEmpty,
                      'bg-neutral-white': !isOdd,
                      'bg-neutral-lightest-two': isOdd,
                    },
                    className
                  );

                return {
                  id: project.id,
                  'data-cy': `linked-project-${project.id}`,
                  onClick: () => onRowClick(project.id, hasAccess),
                  className: tailwindOverride('w-full', {
                    'cursor-pointer': !inlineEditingProjectStatusProgramDetail,
                  }),
                  cells: [
                    {
                      content: (
                        <span title={project.projectNumber?.toString()}>
                          {formatProjectNumber(
                            project.projectNumber ? project.projectNumber : 0
                          )}
                        </span>
                      ),
                      className: stickyCellsStyles('w-24 truncate'),
                    },
                    {
                      content: inlineEditingProjectStatusProgramDetail ? (
                        <LinkTableRowCell
                          value={project.title}
                          to={`${PATHS.PROJECT_PAGE}/${project.id}`}
                        />
                      ) : (
                        <span title={project.title}>{project.title}</span>
                      ),
                      className: stickyCellsStyles(
                        'w-full max-w-60 min-w-60 left-0 truncate'
                      ),
                    },
                    {
                      content: inlineEditingProjectStatusProgramDetail
                        ? getStatusComponent(
                            project,
                            canEditStatus,
                            hasEditPermission,
                            isOnlyStatusViewMode
                          )
                        : getStatusPill(project.status),
                      className: `${
                        inlineEditingProjectStatusProgramDetail
                          ? 'w-20'
                          : 'w-12'
                      } p-0`,
                    },
                    {
                      content: intl.get(
                        `PROJECT_DETAIL.PRIORITY_OPTIONS.${project.priority?.toUpperCase()}`
                      ),
                      className: 'w-30',
                    },
                    {
                      content: (
                        <ProgressBar
                          maxValue={decideMaxValueBasedOnProjectStatus(
                            project.status,
                            completedTasksCount,
                            incompletedTasksCount
                          )}
                          currentValue={decideCurrentProgressValueBasedOnProjectStatus(
                            project.status,
                            completedTasksCount
                          )}
                          barClassName='shadow-inner h-2'
                          containerClassName='w-full'
                          progressClassName='bg-success-darker h-2'
                          percentageNumberClassName='text-neutral-black bg-transparent text-xs'
                        />
                      ),
                      className: 'w-50 min-w-40',
                    },
                    {
                      content: startDate,
                      className: 'truncate w-30',
                    },
                    {
                      content: targetCompletionDate,
                      className: 'truncate w-30',
                    },
                    {
                      content: actualCompletionDate,
                      className: 'truncate w-30',
                    },
                    {
                      content: (
                        <div
                          className={classnames('h-full text-center relative', {
                            'w-16': !isTableEmpty,
                            'w-full': isTableEmpty,
                          })}
                          onClick={(
                            event: React.MouseEvent<HTMLInputElement>
                          ) => event.stopPropagation()}
                        >
                          {isLDUser && (
                            <div className='hidden group-hover:flex'>
                              {/*
                                CGP-118: gate unlink on program-level write access only,
                                mirroring the "Link Projects" gate in ProgramContent.tsx.
                                A user who can link a project to a program they own must
                                also be able to unlink it, regardless of project ownership
                                — do NOT re-add a per-project check (e.g. projectIsReadOnly).
                              */}
                              {userHasWriteAccess && !isReadOnly && (
                                <Tooltip
                                  openMode='hover1'
                                  timeout={0}
                                  showPopper
                                  position='topCenter'
                                  contentProps={{
                                    className: 'px-3 py-2 text-sm z-50',
                                  }}
                                  usePortal
                                  trigger={
                                    <IconButton
                                      src={UnlinkContent}
                                      data-cy={`unlink-project-${project.id}`}
                                      iconClassname='pointer-events-none'
                                      className='text-base ml-2 text-neutral-dark p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                                      onClick={() => unlinkProject(project.id)}
                                    />
                                  }
                                >
                                  {intl.get('PROGRAM_PAGE.UNLINK')}
                                </Tooltip>
                              )}
                              {hasAccess && (
                                <Tooltip
                                  openMode='hover1'
                                  timeout={0}
                                  showPopper
                                  position='topCenter'
                                  contentProps={{
                                    className:
                                      'px-3 py-2 text-sm z-50 absolute',
                                  }}
                                  usePortal
                                  trigger={
                                    <IconButton
                                      name={
                                        !projectIsReadOnly
                                          ? 'pencil-outline'
                                          : 'eye'
                                      }
                                      iconClassname='pointer-events-none'
                                      className='text-base text-neutral-dark ml-2 p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                                      onClick={() =>
                                        onSelectProject(project.id)
                                      }
                                    />
                                  }
                                >
                                  {!projectIsReadOnly
                                    ? intl.get('SIDE_PANEL.QUICK_EDIT')
                                    : intl.get('SIDE_PANEL.QUICK_VIEW')}
                                </Tooltip>
                              )}
                            </div>
                          )}
                        </div>
                      ),
                      className: stickyCellsStyles(classnames('w-12 right-0')),
                    },
                  ],
                };
              }
            ),
          }}
          emptyComponent={
            projectsForProgramStatus === SLICE_STATUS.FAILED ? (
              <TableEmptyErrorState />
            ) : !isEmpty(searchValue) || !isEmpty(filterValues) ? (
              <RenderNoRecords
                imageSrc={
                  !isEmpty(searchValue) ? noSearchResults : noFilterResults
                }
                className={
                  !isEmpty(searchValue)
                    ? 'pr-48 h-empty-table-body'
                    : 'h-empty-filtered-table-body'
                }
                caption={intl.get('PROGRAM_PAGE.TABLE.NO_RECORDS')}
              />
            ) : (
              <RenderNoRecords
                dataCy='empty-projects-table'
                imageSrc={emptyProjectsTable}
                imageClassName='-ml-4'
                className='pr-48 h-empty-table-body'
                caption={intl.get('PROGRAM_PAGE.TABLE.NO_LINKED_PROJECTS')}
                labelClassName='mt-0'
              />
            )
          }
        />
      </div>
      {selectedProject && (
        <>
          <CancelProject
            shouldDisplayModal={displayCancelModal}
            closeModalCallback={handleCloseCancelModal}
            confirmCancelProject={handleConfirmCancelProject}
            cancelReason={selectedProject.cancelReason}
            cancellationDate={selectedProject.cancellationDate}
          />

          <PutProjectOnHoldModal
            shouldDisplayModal={displayOnHoldModal}
            onClose={handleCloseOnHoldModal}
            onCancelCallback={handleCloseOnHoldModal}
            onConfirm={handleConfirmOnHoldProject}
            holdReason={selectedProject.holdReason}
          />

          <CloseProject
            shouldDisplayModal={displayCloseModal}
            closeModalCallback={handleCloseCloseModal}
            confirmCloseProject={handleConfirmCloseProject}
            project={selectedProject}
          />
        </>
      )}
    </div>
  );
};

export default ProgramProjectsTable;
